import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { calculateFirstOrderPlaceDate, getWeekdayFromISODate } from '@theme/jffd-og-date-utils';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #boundOnSellingPlanChange = this.onSellingPlanChange.bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener('change', this.#boundOnSellingPlanChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener('change', this.#boundOnSellingPlanChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    cartTotal?.shimmer();

    this.#postCartChange(
      { line, quantity },
      {
        line,
        onSuccess: () => {
          cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        },
      }
    );
  }

  /**
   * Updates the selling plan for a cart line.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string|null} config.sellingPlan - The selling plan id, or null to remove it (one-time purchase).
   * @param {string} [config.sellingPlanName] - Display name of the plan (bundles only; stored as _bundle_selling_plan_name).
   * @param {string} [config.bundleId] - The bundle group id when the line belongs to a bundle.
   */
  updateSellingPlan(config) {
    const { line, sellingPlan } = config;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('selling-plan-change:user-action');
    const { cartTotal } = this.refs;

    cartTotal?.shimmer();
    if (config.bundleId) {
      this.#updateBundleSellingPlan({
        line,
        sellingPlan,
        sellingPlanName: config.sellingPlanName || '',
        bundleId: config.bundleId,
        cartPerformaceUpdateMarker,
      });
      return;
    }

    this.#postCartChange(
      { line, selling_plan: sellingPlan },
      {
        line,
        endpoint: '/cart/change.js',
        onSuccess: () => {
          cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        },
      }
    );
  }

  async #updateSellingPlanWithFirstOrderDate(config) {
    const { line, sellingPlan, frequencyDays, lineKey, leadTimeDays, cartPerformaceUpdateMarker } = config;
    this.#disableCartItems();

    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) throw new Error(`Cart request failed: ${cartResponse.status}`);

      const cart = await cartResponse.json();
      const lineIndex = cart.items.findIndex((item) => item.key === lineKey);
      if (lineIndex === -1) throw new Error(`Cart line not found: ${lineKey}`);

      const item = cart.items[lineIndex];
      const properties = { ...(item.properties || {}) };
      const sellDay = getWeekdayFromISODate(properties.__og_first_order_place_date);
      if (!sellDay) throw new Error('Ordergroove first order place date is missing or invalid');

      const firstOrderPlaceDate = calculateFirstOrderPlaceDate(sellDay, frequencyDays, leadTimeDays);
      if (!firstOrderPlaceDate) throw new Error('Unable to calculate Ordergroove first order place date');
      properties.__og_first_order_place_date = firstOrderPlaceDate;

      this.#postCartChange(
        {
          line: lineIndex + 1,
          selling_plan: sellingPlan,
          properties,
        },
        {
          line: lineIndex + 1,
          endpoint: '/cart/change.js',
          onSuccess: () => {
            cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
          },
        }
      );
    } catch (error) {
      console.error('Failed to update Ordergroove first order place date:', error);
      // Still apply the plan so the cart never silently keeps the old frequency.
      this.#postCartChange(
        { id: lineKey, selling_plan: sellingPlan },
        {
          line,
          endpoint: '/cart/change.js',
          onSuccess: () => {
            cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
          },
        }
      );
    }
  }

  /**
   * Applies a selling plan change to a bundle. Children carry the plan plus the
   * Ordergroove grouping key; the $0 parent line stays plan-less (Ordergroove
   * would otherwise create a stray standalone subscription for it) and is
   * updated last so the normal cart-change path refreshes sections.
   * @param {Object} config - The config.
   * @param {number} config.line - The bundle parent line.
   * @param {string|null} config.sellingPlan - The selected plan id, or null for Ship once.
   * @param {string} config.sellingPlanName - The selected plan's display name.
   * @param {string} config.bundleId - The bundle group id.
   * @param {*} config.cartPerformaceUpdateMarker - The performance marker.
   */
  async #updateBundleSellingPlan(config) {
    const { line, sellingPlan, sellingPlanName, bundleId, cartPerformaceUpdateMarker } = config;

    try {
      const cartResponse = await fetch('/cart.js');
      let cart = await cartResponse.json();
      const isBundleLine = (item) => item.properties?._bundle_id === bundleId;
      const isParentLine = (item) => item.properties?._bundle_is_parent === 'true';

      const parentLine = cart.items.find((item) => isBundleLine(item) && isParentLine(item));
      if (!parentLine) return;
      const childLines = cart.items.filter((item) => isBundleLine(item) && !isParentLine(item));

      // /cart/change.js replaces the whole property set, so always start from
      // the line's current properties. Mirrors jffd-bundle-product.js: when
      // subscribing, children get __og_parent_product_id (never the parent);
      // Ship once removes the subscription properties everywhere.
      const mergeProperties = (item, isParent) => {
        const properties = { ...(item.properties || {}) };
        if (sellingPlan) {
          properties._bundle_selling_plan_name =
            sellingPlanName || properties._bundle_selling_plan_name || '';
          if (isParent) {
            delete properties.__og_parent_product_id;
          } else {
            properties.__og_parent_product_id = `${parentLine.variant_id}:${bundleId}`;
          }
        } else {
          delete properties._bundle_selling_plan_name;
          delete properties.__og_parent_product_id;
        }
        return properties;
      };

      for (const childLine of childLines) {
        const childLineIndex = cart.items.findIndex((item) => item.key === childLine.key);
        if (childLineIndex === -1) continue;

        const response = await fetch(
          '/cart/change.js',
          fetchConfig('json', {
            body: JSON.stringify({
              line: childLineIndex + 1,
              quantity: childLine.quantity,
              selling_plan: sellingPlan,
              properties: mergeProperties(childLine, false),
            }),
          })
        );
        cart = await response.json();
        if (cart.errors) {
          resetShimmer(this);
          this.#handleCartError(line, cart);
          return;
        }
      }

      const parentLineIndex = cart.items.findIndex((item) => isBundleLine(item) && isParentLine(item));
      if (parentLineIndex === -1) return;
      const currentParent = cart.items[parentLineIndex];

      // Explicit null keeps the parent plan-less and also clears the plan from
      // legacy carts built before the parent line stopped carrying one.
      this.#postCartChange(
        {
          line: parentLineIndex + 1,
          quantity: currentParent.quantity,
          selling_plan: null,
          properties: mergeProperties(currentParent, true),
        },
        {
          line: parentLineIndex + 1,
          endpoint: '/cart/change.js',
          onSuccess: () => {
            cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
          },
        }
      );
    } catch (error) {
      resetShimmer(this);
      console.error('Failed to update bundle selling plans:', error);
    }
  }

  /**
   * Handles a selling plan or ship-once change for a cart line.
   * @param {Event} event
   */
  onSellingPlanChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!this.contains(target)) return;
    if (!target.matches('select[name="selling_plan"]') && !target.matches('input[name^="purchase_option"]')) return;

    const container = target.closest('[data-line-index]');
    if (!(container instanceof HTMLElement)) return;

    const line = Number(container.dataset.lineIndex) + 1;
    if (!line) return;

    const isFrequencyChange = target.matches('select[name="selling_plan"]');

    const subscriptionRadio = /** @type {HTMLInputElement|null} */ (
      container.querySelector('input[name^="purchase_option"][value="subscription"]')
    );

    if (isFrequencyChange && subscriptionRadio) {
      subscriptionRadio.checked = true;
    }

    const purchaseOption = /** @type {HTMLInputElement|null} */ (
      container.querySelector('input[name^="purchase_option"]:checked')
    )?.value;

    let sellingPlan = null;
    let sellingPlanName = '';
    if (purchaseOption === 'subscription') {
      const select = /** @type {HTMLSelectElement|null} */ (container.querySelector('select[name="selling_plan"]'));
      sellingPlan = select?.value ?? null;
      const option = select ? select.options[select.selectedIndex] : null;
      // Prefer the Shopify plan name stamped on the option; fall back to the
      // option text with whitespace collapsed and a trailing "(...)" removed.
      sellingPlanName =
        option?.dataset.planName?.trim() ||
        (option?.textContent || '')
          .replace(/\s+/g, ' ')
          .replace(/\s*\([^)]*\)\s*$/, '')
          .trim();
    }

    const bundleId = container.closest('tr[data-bundle-id]')?.dataset.bundleId;
    const row = container.closest('tr[data-key]');
    const frequencyDays = Number(
      /** @type {HTMLSelectElement|null} */ (container.querySelector('select[name="selling_plan"]'))
        ?.selectedOptions?.[0]?.dataset.frequencyDays
    );

    if (sellingPlan && row?.dataset.ogFirstOrderPlaceDate && frequencyDays > 0 && !bundleId) {
      const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('selling-plan-change:user-action');
      this.refs.cartTotal?.shimmer();
      this.#updateSellingPlanWithFirstOrderDate({
        line,
        sellingPlan,
        frequencyDays,
        lineKey: row.dataset.key,
        leadTimeDays: row.dataset.ogFirstDeliveryLeadTime,
        cartPerformaceUpdateMarker,
      });
      return;
    }

    this.updateSellingPlan({ line, sellingPlan, sellingPlanName, bundleId });
  }

  /**
   * Shared cart /change.js POST + section morph logic, used by both quantity and selling plan updates.
   * @param {Object} body - Extra body params to merge into the request (e.g. { quantity } or { selling_plan }).
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.endpoint] - Optional explicit endpoint override. Defaults to Theme.routes.cart_change_url.
  * @param {() => void} [config.onSuccess] - Called after a successful update.
   */
  #postCartChange(body, config) {
    const { line, onSuccess, endpoint } = config;

    this.#disableCartItems();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const requestBody = JSON.stringify({
      ...body,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    const url = endpoint || Theme.routes.cart_change_url;

    fetch(url, fetchConfig('json', { body: requestBody }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const sectionHtml = parsedResponseText.sections?.[this.sectionId];
        if (!sectionHtml) {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          sectionHtml,
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

          morphSection(this.sectionId, sectionHtml, this.isDrawer ? 'hydration' : 'full');
          this.#updateCartTotal(newSectionHTML);

        this.#updateCartQuantitySelectorButtonStates();

        // Ensure the onSuccess callback runs after the DOM mutations from morphSection
        // have been applied and the browser has had a chance to paint. Wrap in a
        // try/catch to avoid bubbling errors from consumer code.
        if (typeof onSuccess === 'function') {
          try {
            requestAnimationFrame(() => {
              try {
                onSuccess();
              } catch (e) {
                console.error('onSuccess callback error', e);
              }
            });
          } catch (e) {
            console.error('Failed to schedule onSuccess callback', e);
          }
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      const newSectionHTML = new DOMParser().parseFromString(cartItemsHtml, 'text/html');

      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartTotal(newSectionHTML);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Updates the cart total from the refreshed section markup.
   * @param {Document} sectionDocument - The refreshed cart section document.
   */
  #updateCartTotal(sectionDocument) {
    const newCartTotal = sectionDocument.querySelector('[ref="cartTotal"]');
    const cartTotal = this.querySelector('[ref="cartTotal"]');

    if (!newCartTotal || !cartTotal) return;

    cartTotal.textContent = newCartTotal.textContent;
    cartTotal.setAttribute('value', newCartTotal.getAttribute('value') ?? '');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
